/**
 * ControlDimensionalF2 — Medir, firmar y certificar (31-ago-2026).
 *
 * F1 dejó definido en el catálogo QUÉ se mide de cada pieza y con qué política. Esto es
 * la otra mitad: el taller anota los valores, alguien los aprueba o los rechaza, y de eso
 * depende que la OT se pueda cerrar.
 *
 * Lo que la pantalla NO hace, a propósito:
 *   · No decide si un valor está dentro de tolerancia. Lo escribe el servidor al guardar
 *     y acá sólo se muestra. Si lo calculara el navegador, el veredicto lo estaría
 *     firmando quien mide.
 *   · No deja aprobar con una cota CRÍTICA fuera; el botón ni aparece. El servidor lo
 *     rechaza igual —ésa es la defensa de verdad— pero ofrecer un botón que va a fallar
 *     es hacerle perder el tiempo a alguien que está parado frente a un torno.
 *
 * Si la pieza no tiene cotas definidas, no se muestra nada: no hay nada que medir hasta
 * que alguien decida qué se le mide, y un formulario vacío invita a inventar.
 */
import React, { useCallback, useEffect, useState } from "react";
import backend from "../../backend";

const C = {
  ok:    "#2ecc71",
  mal:   "#e74c3c",
  tenue: "#8a8a8a",
  linea: "#2e2e2e",
  fondo: "#161616",
  texto: "#ddd",
};

const ESTADO = {
  sin_medir: { texto: "Sin medir",  color: C.tenue },
  medido:    { texto: "Medido, falta aprobar", color: "#f39c12" },
  aprobado:  { texto: "Aprobado",   color: C.ok },
  rechazado: { texto: "Rechazado",  color: C.mal },
};

const rango = c => {
  const mas = Math.abs(Number(c.toleranciaMas) || 0);
  const menos = Math.abs(Number(c.toleranciaMenos) || 0);
  return `${c.nominal} +${mas} / −${menos} ${c.unidad || c.unidadMedida || "mm"}`;
};

const ControlDimensionalF2 = ({ pi, readOnly = false, onCambio }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [midiendo, setMidiendo] = useState(null);   // número de unidad en edición
  const [borrador, setBorrador] = useState({});
  const [guardando, setGuardando] = useState(false);

  const piId = pi?._id;

  const traer = useCallback(async () => {
    if (!piId) return;
    try {
      const { data } = await backend.get(`control-dimensional/instancia/${piId}`);
      setDatos(data);
    } catch (e) {
      setError(e?.response?.data || e.message);
    } finally {
      setCargando(false);
    }
  }, [piId]);

  useEffect(() => { traer(); }, [traer]);

  const cotas = datos?.pieza?.cotasCriticas || [];
  const control = datos?.control || {};
  const mediciones = control.mediciones || [];
  const cantidad = datos?.instancia?.cantidad || 1;
  const politica = datos?.pieza?.politicaControl || {};
  const exigidas = politica.modo === "cien_por_ciento"
    ? cantidad
    : Math.min(Number(politica.tamanoMuestra) || 3, cantidad);

  const criticaFuera = mediciones.some(m =>
    (m.valores || []).some(v => v.critica && v.dentro === false)
  );

  const llamar = async (fn) => {
    setError("");
    setGuardando(true);
    try {
      await fn();
      await traer();
      onCambio?.();
    } catch (e) {
      setError(e?.response?.data || e.message);
    } finally {
      setGuardando(false);
    }
  };

  const guardarMedicion = unidad =>
    llamar(async () => {
      await backend.post(`control-dimensional/instancia/${piId}/medicion`, {
        unidad,
        valores: cotas.map(c => ({ etiqueta: c.etiqueta, valor: borrador[c.etiqueta] })),
        notas: borrador.__notas || "",
      });
      setMidiendo(null);
      setBorrador({});
    });

  const abrirUnidad = unidad => {
    const ya = mediciones.find(m => m.unidad === unidad);
    const inicial = {};
    for (const v of ya?.valores || []) inicial[v.etiqueta] = v.valor;
    inicial.__notas = ya?.notas || "";
    setBorrador(inicial);
    setMidiendo(unidad);
  };

  if (cargando || !datos) return null;
  if (!cotas.length) return null;

  const meta = ESTADO[control.estado] || ESTADO.sin_medir;

  return (
    <div style={{
      background: "#1c1c1c", border: `1px solid ${C.linea}`, borderRadius: 6,
      padding: "10px 14px", marginBottom: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: ".06em" }}>
          📐 Control dimensional
        </span>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 9,
          background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55`,
        }}>{meta.texto}</span>
        <span style={{ fontSize: 10, color: C.tenue }}>
          {politica.modo === "cien_por_ciento" ? "100% de las unidades" : `muestreo de ${politica.tamanoMuestra}`}
          {" · "}{mediciones.length} de {exigidas} {exigidas === 1 ? "unidad medida" : "unidades medidas"}
        </span>
        <span style={{ flex: 1 }} />
        {control.estado === "aprobado" && (
          <a
            href={`${backend.defaults.baseURL || ""}`}
            onClick={async e => {
              e.preventDefault();
              // Se pide con la sesión puesta y se abre como blob: el <a> directo iría sin
              // el token y el servidor respondería 401.
              const r = await backend.get(`control-dimensional/instancia/${piId}/certificado`, { responseType: "blob" });
              window.open(URL.createObjectURL(r.data), "_blank");
            }}
            style={{ fontSize: 11, color: "#3498db" }}
          >📄 Certificado</a>
        )}
      </div>

      {error && (
        <div style={{ padding: "5px 10px", marginBottom: 8, background: "#2d0000",
                      border: "1px solid #4a0000", borderRadius: 4, color: C.mal, fontSize: 10 }}>
          ⚠ {String(error)}
        </div>
      )}

      {control.estado === "rechazado" && control.motivoRechazo && (
        <div style={{ padding: "5px 10px", marginBottom: 8, background: "#2d0000",
                      border: "1px solid #4a0000", borderRadius: 4, color: "#f5b7b1", fontSize: 10 }}>
          Rechazado por {control.revisadoPor}: {control.motivoRechazo}
        </div>
      )}

      {/* Una fila por unidad de la PI: lo que se mide son piezas, no líneas de la orden. */}
      {Array.from({ length: cantidad }, (_, i) => i + 1).map(unidad => {
        const m = mediciones.find(x => x.unidad === unidad);
        const editando = midiendo === unidad;
        const fuera = (m?.valores || []).filter(v => v.dentro === false);
        return (
          <div key={unidad} style={{
            background: C.fondo, border: `1px solid ${C.linea}`, borderRadius: 5,
            padding: "7px 10px", marginBottom: 5,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <b style={{ fontSize: 11, color: C.texto }}>Unidad {unidad}</b>
              {m ? (
                <>
                  <span style={{ fontSize: 10, color: fuera.length ? C.mal : C.ok }}>
                    {fuera.length ? `${fuera.length} fuera de tolerancia` : "todo dentro"}
                  </span>
                  <span style={{ fontSize: 10, color: C.tenue }}>
                    {m.medidoPor} · {m.medidoEn ? new Date(m.medidoEn).toLocaleDateString("es-CL") : ""}
                  </span>
                </>
              ) : (
                <span style={{ fontSize: 10, color: C.tenue }}>sin medir</span>
              )}
              <span style={{ flex: 1 }} />
              {!readOnly && !editando && (
                <button type="button" onClick={() => abrirUnidad(unidad)} style={btn("#3498db")}>
                  {m ? "Corregir" : "Medir"}
                </button>
              )}
            </div>

            {m && !editando && (
              <table style={{ width: "100%", marginTop: 5, borderCollapse: "collapse" }}>
                <tbody>
                  {(m.valores || []).map(v => (
                    <tr key={v.etiqueta}>
                      <td style={td}>{v.etiqueta}{v.critica ? " ⚠" : ""}</td>
                      <td style={{ ...td, color: C.tenue, textAlign: "right" }}>{rango(v)}</td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{v.valor}</td>
                      <td style={{ ...td, textAlign: "right", color: v.dentro ? C.ok : C.mal, width: 60 }}>
                        {v.dentro ? "dentro" : "FUERA"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {editando && (
              <div style={{ marginTop: 6 }}>
                {cotas.map(c => (
                  <div key={c.etiqueta} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: C.texto, flex: 1 }}>
                      {c.etiqueta}{c.critica ? " ⚠" : ""}
                    </span>
                    <span style={{ fontSize: 10, color: C.tenue }}>{rango(c)}</span>
                    <input
                      type="number" step="any" autoFocus={c === cotas[0]}
                      value={borrador[c.etiqueta] ?? ""}
                      onChange={e => setBorrador(b => ({ ...b, [c.etiqueta]: e.target.value }))}
                      style={{ width: 100, background: "#111", border: `1px solid ${C.linea}`,
                               borderRadius: 4, padding: "3px 7px", color: C.texto, fontSize: 12,
                               textAlign: "right" }}
                    />
                  </div>
                ))}
                <input
                  placeholder="Nota (instrumento, condiciones…)"
                  value={borrador.__notas ?? ""}
                  onChange={e => setBorrador(b => ({ ...b, __notas: e.target.value }))}
                  style={{ width: "100%", background: "#111", border: `1px solid ${C.linea}`,
                           borderRadius: 4, padding: "3px 7px", color: C.texto, fontSize: 11, marginTop: 2 }}
                />
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <button type="button" disabled={guardando} onClick={() => guardarMedicion(unidad)} style={btn(C.ok)}>
                    Guardar unidad {unidad}
                  </button>
                  <button type="button" onClick={() => { setMidiendo(null); setBorrador({}); }} style={btn(C.tenue)}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {!readOnly && mediciones.length > 0 && control.estado !== "aprobado" && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
          {criticaFuera ? (
            <span style={{ fontSize: 10, color: C.mal }}>
              Hay una cota crítica fuera de tolerancia: esto no se aprueba. Corregí la medición o rechazá el control.
            </span>
          ) : (
            <button type="button" disabled={guardando} style={btn(C.ok)}
              onClick={() => llamar(() => backend.post(`control-dimensional/instancia/${piId}/aprobar`, {}))}>
              ✔ Aprobar control
            </button>
          )}
          <button type="button" disabled={guardando} style={btn(C.mal)}
            onClick={() => {
              const motivo = window.prompt("¿Por qué se rechaza el control?");
              if (motivo && motivo.trim()) {
                llamar(() => backend.post(`control-dimensional/instancia/${piId}/rechazar`, { motivo }));
              }
            }}>
            ✕ Rechazar
          </button>
        </div>
      )}

      {control.estado === "aprobado" && (
        <div style={{ fontSize: 10, color: C.tenue, marginTop: 6 }}>
          Aprobado por {control.revisadoPor} el{" "}
          {control.revisadoEn ? new Date(control.revisadoEn).toLocaleString("es-CL") : "—"}
        </div>
      )}
    </div>
  );
};

const btn = color => ({
  background: `${color}22`, color, border: `1px solid ${color}55`,
  borderRadius: 4, padding: "3px 10px", fontSize: 10, fontWeight: 600, cursor: "pointer",
});

const td = { fontSize: 11, color: C.texto, padding: "2px 4px", borderTop: "1px solid #222" };

export default ControlDimensionalF2;
